import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { 
  LayoutDashboard, 
  Users, 
  Calendar, 
  ClipboardList,
  ListTodo,
  Bell,
  BarChart3,
  FileText,
  UserCircle,
  Settings,
  LogOut,
  Activity,
  Wallet,
  Contact,
  FileSignature,
  PartyPopper,
  Briefcase
} from "lucide-react";
import { apiFetch } from "../utils/api";
import { puoVedereAreaCommerciale } from "../utils/areaCommerciale";
import { toast } from "sonner";

const PRIMARY_ITEMS = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/tasks", label: "Gestione", icon: ListTodo },
  { path: "/notifiche", label: "Notifiche", icon: Bell },
  { path: "/clients", label: "Clienti", icon: Users },
  { path: "/scheda-cliente", label: "Scheda", icon: ClipboardList },
  { path: "/calendar", label: "Calendario", icon: Calendar },
  { path: "/social", label: "Social", icon: Activity },
  { path: "/crm", label: "CRM", icon: Contact },
  { path: "/contratti", label: "Contratti", icon: FileSignature },
  { path: "/contratti-eventi", label: "Eventi", icon: PartyPopper },
];

// Solo per commerciali e direzione: stesse regole di GET /area-commerciale.
const AREA_COMMERCIALE_ITEM = { path: "/area-commerciale", label: "Commerciale", icon: Briefcase };

const PM_ITEMS = [
  { path: "/compensi", label: "Compensi", icon: Wallet },
  { path: "/stats", label: "Statistiche", icon: BarChart3 },
  { path: "/reports", label: "Report", icon: FileText },
  { path: "/team", label: "Team", icon: UserCircle },
  { path: "/settings", label: "Impostazioni", icon: Settings },
];

const AMM_ITEMS = [
  { path: "/compensi", label: "Compensi", icon: Wallet },
  { path: "/stats", label: "Statistiche", icon: BarChart3 },
  { path: "/reports", label: "Report", icon: FileText },
];

export default function MobileNav({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  // Rosso solo per ciò che è in ritardo o è un alert: il resto è accento, così
  // il rosso torna a voler dire qualcosa (vedi /notifications/unread-count).
  const [urgente, setUrgente] = useState(false);

  const isDirettore = user?.role === "direttore_tecnico" || user?.role === "direttore_commerciale" || !!user?.is_admin;
  const isPM = user?.role === "pm" || isDirettore || (user?.secondary_roles || []).includes("pm") || !!user?.is_admin;
  const isAMM = user?.role === "amm" || isDirettore || (user?.secondary_roles || []).includes("amm") || !!user?.is_admin;

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await apiFetch("/notifications/unread-count");
        if (res.ok) {
          const data = await res.json();
          setUnreadCount(data.total || 0);
          setUrgente(!!data.urgente);
        }
      } catch (e) {/* ignore */}
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    // La pagina Notifiche lo emette dopo ogni lettura o chiusura.
    window.addEventListener("notifiche-aggiornate", fetchCount);
    return () => {
      clearInterval(id);
      window.removeEventListener("notifiche-aggiornate", fetchCount);
    };
  }, [location.pathname]);

  // Non un semplice prefisso: /contratti-eventi accenderebbe anche /contratti.
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleLogout = async () => {
    try {
      await apiFetch("/auth/logout", { method: "POST" });
    } catch (e) {/* ignore */}
    localStorage.removeItem("session_token");
    setUser?.(null);
    toast.success("Logout effettuato");
    navigate("/login");
  };

  const items = [
    PRIMARY_ITEMS[0],
    ...(puoVedereAreaCommerciale(user) ? [AREA_COMMERCIALE_ITEM] : []),
    ...PRIMARY_ITEMS.slice(1),
    ...(isPM ? PM_ITEMS : isAMM ? AMM_ITEMS : []),
  ];

  return (
    <nav className="mobile-nav" data-testid="mobile-nav">
      <div
        className="flex items-center gap-1 overflow-x-auto px-2 py-2 h-16 scrollbar-hide"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {items.map((item) => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={`
              relative flex flex-col items-center justify-center px-3 py-1 min-w-[64px] flex-shrink-0 rounded-lg transition-colors
              ${isActive(item.path) 
                ? "text-indigo-600 bg-indigo-50" 
                : "text-muted-foreground hover:text-foreground"
              }
            `}
            data-testid={`mobile-nav-${item.path.slice(1)}`}
          >
            <item.icon className={`w-5 h-5 ${isActive(item.path) ? "text-indigo-600" : ""}`} />
            {item.path === "/notifiche" && unreadCount > 0 && (
              <span className={`absolute top-0 right-1 min-w-[16px] h-4 px-1 rounded-full text-white text-[9px] font-bold flex items-center justify-center ${urgente ? "bg-[#c0392b]" : "bg-[#5b5bd6]"}`} data-testid="mobile-notif-badge">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
            <span className="text-[10px] mt-0.5 font-medium whitespace-nowrap">{item.label}</span>
          </button>
        ))}
        <button
          onClick={handleLogout}
          className="relative flex flex-col items-center justify-center px-3 py-1 min-w-[64px] flex-shrink-0 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
          data-testid="mobile-nav-logout"
        >
          <LogOut className="w-5 h-5" />
          <span className="text-[10px] mt-0.5 font-medium">Logout</span>
        </button>
      </div>
    </nav>
  );
}
